import type {
  RosterItemResponse,
  RosterItemResponseOrganizationRosterResponseRelatedPagedData,
  SportFilter,
} from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { parseJerseySortValue } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getHighSchoolSportLabel } from '@on3/ui-lib/utils/url';
import {
  getStatusType,
  StatusTypeValue,
} from 'components/College/Interests/Commits/CommitList';
import { HighSchoolNav } from 'components/HighSchool/Nav/HighSchoolNav';
import { RosterWrapper } from 'hocs/Roster/RosterWrapper';
import { useCallback, useState } from 'react';

import { HighSchoolRosterHeader } from './HighSchoolRosterHeader';
import { HighSchoolRosterItem } from './HighSchoolRosterItem';
import styles from './HighSchoolRosterItem.module.scss';

interface IHighSchoolRosterProps {
  highSchoolRoster?: RosterItemResponseOrganizationRosterResponseRelatedPagedData;
  title?: string;
  filters: {
    sports?: SportFilter[];
    years?: number[];
  };
}

export const HighSchoolRoster = ({
  highSchoolRoster,
  title,
  filters,
}: IHighSchoolRosterProps) => {
  const { list, relatedModel } = highSchoolRoster || {};
  const { orgResponse, sport, year } = relatedModel || {};
  const [selected, setSelected] = useState('rating');
  const [ascending, setAscending] = useState(true);
  const [rosterList, setRosterList] = useState(list);
  const { sports, years } = filters;

  const useSortList = (listItem?: RosterItemResponse[] | null) =>
    useCallback(
      (sortBy: keyof RosterItemResponse | string, asc: boolean) => {
        if (!listItem?.length) {
          return listItem;
        }

        const isName = sortBy === 'lastName';
        const isHeight = sortBy === 'height';
        const isWeight = sortBy === 'weight';
        const isStatus = sortBy === 'status';
        const isPosition = sortBy === 'position';
        const isRating = sortBy === 'rating';
        const isNilValue = sortBy === 'nilValue';
        const isJersey = sortBy === 'jerseyNumber';
        const isAthleteVerified = sortBy === 'athleteVerified';

        const sortedList = [...listItem]?.sort((a, b) => {
          let setA = null;
          let setB = null;

          if (isName) {
            setA = a.player?.lastName;
            setB = b.player?.lastName;
          }

          if (isWeight) {
            setA = a.player?.weight;
            setB = b.player?.weight;
          }

          if (isHeight) {
            setA = a.player?.height;
            setB = b.player?.height;
          }

          if (isPosition) {
            setA = a?.player?.position?.name;
            setB = b?.player?.position?.name;
          }

          if (isJersey) {
            setA = parseJerseySortValue(a?.player?.jerseyNumber);
            setB = parseJerseySortValue(b?.player?.jerseyNumber);
          }

          if (isRating) {
            setA = a.rating?.consensusRating;
            setB = b.rating?.consensusRating;
          }

          if (isNilValue) {
            setA = a?.nilValue?.totalValue || 0;
            setB = b?.nilValue?.totalValue || 0;
          }

          if (isAthleteVerified) {
            setA = a.player?.athleteVerified ? 1 : 0;
            setB = b.player?.athleteVerified ? 1 : 0;
          }

          if (isStatus) {
            const statusA = a.status?.transferredAsset
              ? 'Transferred'
              : a.status?.type;
            const statusB = b.status?.transferredAsset
              ? 'Transferred'
              : b.status?.type;

            const statusTypeA = getStatusType(statusA);
            const statusTypeB = getStatusType(statusB);

            const statusValueA = StatusTypeValue[statusTypeA];
            const statusValueB = StatusTypeValue[statusTypeB];

            setA = statusValueA;
            setB = statusValueB;
          }

          //  puts nulls at the end of list
          if (!setA && setA !== 0) {
            return 1;
          }

          if (!setB && setB !== 0) {
            return -1;
          }

          if (setA < setB) {
            return asc ? 1 : -1;
          }

          if (setA > setB) {
            return asc ? -1 : 1;
          }

          return 0;
        });

        return sortedList;
      },
      [listItem],
    );
  const isRosterList = rosterList?.length;
  const sortList = useSortList(list);
  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { dataset } = e.currentTarget;
      const { sorttype = '', flip = 'false' } = dataset;
      //  flips order of the list
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttype !== selected) {
        setSelected(sorttype);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttype, orderBy);

      sortedList && setRosterList(sortedList);
    },
    [ascending, selected, sortList],
  );
  const sportSlug = slugify(sport?.name || 'football');
  const primaryColor = adjustColorForReadability(
    orgResponse?.primaryColor || '#fe3b1f',
  );

  return (
    <section className={styles.block}>
      <header className={styles.header}>
        <div className={styles.title}>
          <figure>
            <Avatar
              altText={title}
              domain={orgResponse?.defaultAsset?.domain}
              source={orgResponse?.defaultAsset?.source}
              teamAvatar
            />
          </figure>
          <h1>{title}</h1>
        </div>
        <nav className={styles.filterWrapper} style={{ color: primaryColor }}>
          {sports && (
            <Filter
              color={primaryColor}
              name="sport"
              navigate={(_, filterSport) =>
                `/high-school/${orgResponse?.urlSlug}/${filterSport}/${year}/roster/`
              }
              options={sports.map((s) => ({
                label: getHighSchoolSportLabel(s),
                value: slugify(s.name),
              }))}
              value={slugify(sport?.name)}
            />
          )}
          {years && (
            <Filter
              color={primaryColor}
              name="year"
              navigate={(_, filterYear) =>
                `/high-school/${orgResponse?.urlSlug}/${sportSlug}/${filterYear}/roster/`
              }
              options={years.map((y) => ({
                label: String(y),
                value: String(y),
              }))}
              value={String(year)}
            />
          )}
          <HighSchoolNav primaryColor={primaryColor} />
        </nav>
      </header>

      {!!isRosterList && (
        <>
          <HighSchoolRosterHeader
            ascending={ascending}
            onChange={handleChange}
            primaryColor={orgResponse?.primaryColor}
            selected={selected}
          />
          <RosterWrapper roster={rosterList}>
            {({ edit }) => (
              <>
                {rosterList?.map((item) => (
                  <HighSchoolRosterItem
                    handleEdit={edit}
                    key={item.psoKey}
                    rosterItem={item}
                    sport={sport?.name || 'football'}
                  />
                ))}
              </>
            )}
          </RosterWrapper>
        </>
      )}
      {!isRosterList && (
        <div className={styles.noneFound}>
          <p>There are no players available for this selection at this time.</p>
        </div>
      )}
    </section>
  );
};
